/* eslint-disable */
import React from "react";
import { Box, Button, Grid } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";

import MDTypography from "components/MDTypography";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import AppNumberInput from "components/common/AppNumberInput";
import AppFormContext from "components/common/AppFormContext";

import LocalMachineProfitPercentageService from "services/localMachineProfitPercentageService";
import VisitTransactionService from "services/visitTransactionService";
import { hasRent } from "utils/rent";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";
import { secondaryOutlinedButtonSx } from "components/common/buttonStyles";

function normalizePercentageValue(value) {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getPercentageFormValue(value) {
  if (value == null || value === "") return "";

  const parsed = Number(value);
  if (Number.isInteger(parsed)) return String(parsed);

  return String(value);
}

function isProfitPercentageChanged(pp, form) {
  if (!form) return false;

  return (
    normalizePercentageValue(form.ClientPercentage) !== normalizePercentageValue(pp.ClientPercentage) ||
    normalizePercentageValue(form.OwnerPercentage) !== normalizePercentageValue(pp.OwnerPercentage)
  );
}

function getProfitPercentageValidationError(form) {
  const rawClient = form?.ClientPercentage;
  const rawOwner = form?.OwnerPercentage;

  if (
    rawClient === "" ||
    rawClient === null ||
    rawClient === undefined ||
    rawOwner === "" ||
    rawOwner === null ||
    rawOwner === undefined
  ) {
    return "Los porcentajes deben ser números enteros entre 0 y 100 y sumar 100.";
  }

  const clientPercentage = Number(rawClient);
  const ownerPercentage = Number(rawOwner);

  if (
    !Number.isInteger(clientPercentage) ||
    !Number.isInteger(ownerPercentage) ||
    clientPercentage < 0 ||
    clientPercentage > 100 ||
    ownerPercentage < 0 ||
    ownerPercentage > 100 ||
    clientPercentage + ownerPercentage !== 100
  ) {
    return "Los porcentajes deben ser números enteros entre 0 y 100 y sumar 100.";
  }

  return "";
}

function EditProfitPercentagesForm({
  localId,
  localMachineDetails,
  profitPercentages,
  onClose,
  onSuccess,
}) {
  const [forms, setForms] = React.useState({});
  const [editingIds, setEditingIds] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [effectiveDate, setEffectiveDate] = React.useState(getDefaultOperationDate());
  const [generalError, setGeneralError] = React.useState("");
  const [showRequiredErrors, setShowRequiredErrors] = React.useState(false);
  const [latestAffectedMachineCollectionDate, setLatestAffectedMachineCollectionDate] =
    React.useState(null);

  const nonRentedTypeIds = React.useMemo(() => {
    const set = new Set();
    (localMachineDetails || []).forEach((lm) => {
      if (hasRent(lm)) return;

      const typeId =
        lm?.MachineTypeId ??
        lm?.Machine?.MachineTypeId ??
        lm?.MachineType?.Id ??
        lm?.Machine?.MachineType?.Id ??
        null;

      if (typeId != null) set.add(Number(typeId));
    });
    return set;
  }, [localMachineDetails]);

  const editablePercentages = React.useMemo(
    () => (profitPercentages || []).filter((pp) => nonRentedTypeIds.has(Number(pp.MachineTypeId))),
    [profitPercentages, nonRentedTypeIds]
  );

  React.useEffect(() => {
    const initial = {};
    editablePercentages.forEach((pp) => {
      initial[pp.Id] = {
        ClientPercentage: getPercentageFormValue(pp.ClientPercentage),
        OwnerPercentage: getPercentageFormValue(pp.OwnerPercentage),
      };
    });
    setForms(initial);
    setEditingIds([]);
  }, [editablePercentages]);

  const changedPercentages = React.useMemo(
    () =>
      editablePercentages.filter(
        (pp) => editingIds.includes(pp.Id) && isProfitPercentageChanged(pp, forms[pp.Id])
      ),
    [editablePercentages, editingIds, forms]
  );

  const affectedTypeIds = React.useMemo(() => {
    if (changedPercentages.length) {
      return Array.from(new Set(changedPercentages.map((pp) => Number(pp.MachineTypeId))));
    }

    if (editingIds.length) {
      return Array.from(
        new Set(
          editablePercentages
            .filter((pp) => editingIds.includes(pp.Id))
            .map((pp) => Number(pp.MachineTypeId))
        )
      );
    }

    return Array.from(new Set(editablePercentages.map((pp) => Number(pp.MachineTypeId))));
  }, [changedPercentages, editablePercentages, editingIds]);

  const affectedLocalMachines = React.useMemo(
    () =>
      (localMachineDetails || []).filter((lm) => {
        if (hasRent(lm)) return false;

        const typeId =
          lm?.MachineTypeId ??
          lm?.Machine?.MachineTypeId ??
          lm?.MachineType?.Id ??
          lm?.Machine?.MachineType?.Id ??
          null;

        return typeId != null && affectedTypeIds.includes(Number(typeId));
      }),
    [affectedTypeIds, localMachineDetails]
  );

  const minStartDate = React.useMemo(() => {
    let latest = null;
    affectedLocalMachines.forEach((lm) => {
      const parsed = parseDateOnlyToLocalDate(dateOnlyFromDateTime(lm?.StartDateTime));
      if (parsed && (!latest || parsed > latest)) {
        latest = parsed;
      }
    });
    return latest;
  }, [affectedLocalMachines]);

  React.useEffect(() => {
    let cancelled = false;

    const loadLatestAffectedMachineCollectionDate = async () => {
      if (!affectedLocalMachines.length) {
        if (!cancelled) setLatestAffectedMachineCollectionDate(null);
        return;
      }

      try {
        const results = await Promise.all(
          affectedLocalMachines.map((lm) =>
            VisitTransactionService.getLastCollectionByMachineInLocal(lm.Id)
          )
        );

        let latest = null;
        results.forEach((result) => {
          const parsed = parseDateOnlyToLocalDate(dateOnlyFromDateTime(result?.visit?.DateTime));
          if (parsed && (!latest || parsed > latest)) {
            latest = parsed;
          }
        });

        if (!cancelled) {
          setLatestAffectedMachineCollectionDate(latest);
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error al obtener la última recaudación de las máquinas afectadas:", err);
        }
        if (!cancelled) {
          setLatestAffectedMachineCollectionDate(null);
        }
      }
    };

    loadLatestAffectedMachineCollectionDate();
    return () => {
      cancelled = true;
    };
  }, [affectedLocalMachines]);

  const pickerMinDate = React.useMemo(() => {
    if (!minStartDate) return latestAffectedMachineCollectionDate;
    if (!latestAffectedMachineCollectionDate) return minStartDate;
    return latestAffectedMachineCollectionDate > minStartDate
      ? latestAffectedMachineCollectionDate
      : minStartDate;
  }, [latestAffectedMachineCollectionDate, minStartDate]);

  const getEffectiveDateErrorMessage = React.useCallback(() => {
    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }
    if (
      latestAffectedMachineCollectionDate &&
      isBeforeMinOperationDate(effectiveDate, latestAffectedMachineCollectionDate)
    ) {
      return "La fecha seleccionada no puede ser anterior a la última recaudación de las máquinas afectadas.";
    }
    if (minStartDate && isBeforeMinOperationDate(effectiveDate, minStartDate)) {
      return "La fecha seleccionada no puede ser anterior al último movimiento de las máquinas afectadas.";
    }
    return "";
  }, [effectiveDate, latestAffectedMachineCollectionDate, minStartDate]);

  const getApiErrorMessage = React.useCallback((error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.code || "";
    const message =
      error?.response?.data?.error ||
      "No se pudieron actualizar los porcentajes. Intenta nuevamente.";

    if (status === 409 && code === "EFFECTIVE_DATE_BEFORE_LAST_MACHINE_COLLECTION") {
      return "La fecha seleccionada no puede ser anterior a la última recaudación de las máquinas afectadas.";
    }
    if (status === 409 && code === "EFFECTIVE_DATE_BEFORE_CURRENT_LOCAL_MACHINE_START") {
      return "La fecha seleccionada no puede ser anterior al último movimiento de las máquinas afectadas.";
    }
    if (status === 400 && code === "EFFECTIVE_DATE_CANNOT_BE_FUTURE") {
      return "No se puede seleccionar una fecha futura.";
    }
    return message;
  }, []);

  const handleChange = (id, field, value) => {
    setGeneralError("");
    setForms((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const handleStartEditing = (pp) => {
    setGeneralError("");
    setEditingIds((prev) => (prev.includes(pp.Id) ? prev : [...prev, pp.Id]));
    setForms((prev) => ({
      ...prev,
      [pp.Id]: {
        ClientPercentage: getPercentageFormValue(pp.ClientPercentage),
        OwnerPercentage: getPercentageFormValue(pp.OwnerPercentage),
      },
    }));
  };

  const handleCancelEditing = (pp) => {
    setGeneralError("");
    setEditingIds((prev) => prev.filter((id) => id !== pp.Id));
    setForms((prev) => ({
      ...prev,
      [pp.Id]: {
        ClientPercentage: getPercentageFormValue(pp.ClientPercentage),
        OwnerPercentage: getPercentageFormValue(pp.OwnerPercentage),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editablePercentages.length) {
      onClose?.();
      return;
    }
    if (!changedPercentages.length) {
      onClose?.();
      return;
    }

    const effectiveDateError = getEffectiveDateErrorMessage();
    if (effectiveDateError) {
      return;
    }

    setShowRequiredErrors(true);
    if (changedPercentages.some((pp) => forms[pp.Id]?.ClientPercentage === "" || forms[pp.Id]?.OwnerPercentage === "")) {
      setGeneralError("");
      return;
    }

    const percentageError = changedPercentages
      .map((pp) => getProfitPercentageValidationError(forms[pp.Id]))
      .find(Boolean);

    if (percentageError) {
      setGeneralError(percentageError);
      return;
    }

    try {
      setSaving(true);
      setGeneralError("");

      for (const pp of changedPercentages) {
        const currentForm = forms[pp.Id];
        if (!currentForm) continue;

        const payload = {
          ClientPercentage:
            currentForm.ClientPercentage !== "" ? Number(currentForm.ClientPercentage) : null,
          OwnerPercentage:
            currentForm.OwnerPercentage !== "" ? Number(currentForm.OwnerPercentage) : null,
          EffectiveDate: toLocalDateOnlyString(effectiveDate),
        };

        await LocalMachineProfitPercentageService.updateLocalMachineProfitPercentage(pp.Id, payload);
      }

      await onSuccess?.();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error al actualizar porcentajes:", err);
      }
      setGeneralError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!editablePercentages.length) {
    return (
      <Box sx={{ p: 1 }}>
        <MDTypography variant="body2">
          No hay tipos de máquina no alquiladas con porcentajes configurados en este local.
        </MDTypography>
      </Box>
    );
  }

  const effectiveDateError = getEffectiveDateErrorMessage();
  const today = getDefaultOperationDate();

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Box sx={{ mt: 1, minWidth: 0 }}>
        <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Elegí qué tipos querés modificar. Solo se guardarán los que tengan cambios, que se aplicarán a las recaudaciones futuras.
        </MDTypography>

        {editablePercentages.map((pp) => {
          const form = forms[pp.Id] || {};
          const isEditing = editingIds.includes(pp.Id);

          return (
            <Box
              key={pp.Id}
              sx={{ mb: 3, pb: 2, borderBottom: "1px solid rgba(0,0,0,0.08)" }}
            >
              <AppFormContext
                title={pp.MachineType}
                lines={[`Porcentajes actuales · Cliente: ${pp.ClientPercentage}% · Dueño: ${pp.OwnerPercentage}%`]}
                sx={{ borderBottom: 0, pb: 0, mb: 1 }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-start", mb: isEditing ? 1 : 0 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => (isEditing ? handleCancelEditing(pp) : handleStartEditing(pp))}
                  disabled={saving}
                  sx={secondaryOutlinedButtonSx}
                >
                  {isEditing ? "Cancelar edición" : "Editar"}
                </Button>
              </Box>

              {isEditing && (
                <>
                  <MDTypography variant="subtitle2" sx={{ mb: 1 }}>
                    Nuevos porcentajes
                  </MDTypography>
                  <Box sx={{ display: "grid", gap: 2 }}>
                    <AppNumberInput
                      fullWidth
                      label="Porcentaje cliente (%)"
                      required
                      value={form.ClientPercentage ?? ""}
                      onValueChange={(value) => handleChange(pp.Id, "ClientPercentage", value)}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      max={100}
                      error={showRequiredErrors && form.ClientPercentage === ""}
                      helperText={showRequiredErrors && form.ClientPercentage === "" ? "Ingresá un porcentaje." : ""}
                    />

                    <AppNumberInput
                      fullWidth
                      label="Porcentaje dueño (%)"
                      required
                      value={form.OwnerPercentage ?? ""}
                      onValueChange={(value) => handleChange(pp.Id, "OwnerPercentage", value)}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      max={100}
                      error={showRequiredErrors && form.OwnerPercentage === ""}
                      helperText={showRequiredErrors && form.OwnerPercentage === "" ? "Ingresá un porcentaje." : ""}
                    />
                  </Box>
                </>
              )}
            </Box>
          );
        })}

        <Box sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
            <DatePicker
              label="Fecha de modificación"
              value={effectiveDate}
              onChange={(date) => {
                setEffectiveDate(date);
                setGeneralError("");
              }}
              minDate={pickerMinDate || undefined}
              maxDate={today}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(effectiveDateError),
                  helperText: effectiveDateError,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {generalError && (
          <MDTypography variant="body2" color="error" sx={{ mb: 2 }}>
            {generalError}
          </MDTypography>
        )}

        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <CancelButton onClick={onClose} disabled={saving} fullWidth>
                Cancelar
              </CancelButton>
            </Grid>
            <Grid item xs={12} md={6}>
              <AcceptButton type="submit" disabled={saving || Boolean(effectiveDateError)} fullWidth>
                Guardar cambios
              </AcceptButton>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </form>
  );
}

export default EditProfitPercentagesForm;
